import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { faCircleInfo, faHeartPulse } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";

const FinancialWellbeingForm = () => {
  const navigate = useNavigate();
  const axios = useAxiosPrivate();
  const { createInfo, auth, userPolicy, setShowPaymentRedirect } = useAuth();

  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

     const location = useLocation()
        const product = location?.state?.product
        const [hasAccess, setHasAccess] = useState(true)
        const [accessMode, setAccessMode] = useState('fallback') // 'api' | 'fallback'
        const [accessByProduct, setAccessByProduct] = useState({})

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name: auth?.user?.name || "",
      email: auth?.user?.email || "",
      dob: auth?.user?.date_of_birth || "",
      contact: auth?.user?.phone_number || "",
      schoolName: auth?.user?.client?.organisation?.name || "",
      schoolPostcode: auth?.user?.client?.organisation?.postcode || "",
    }));
  }, [auth?.user])

    useEffect(() => {
        setHasAccess(checkProductAccess(product?.title))
    }, [])

    useEffect(() => {
        if (!auth?.token) return
        let cancelled = false
        setAccessMode('fallback')
        axios
            .post('/service-access/check', {
                product_titles: [product.title],
                ...(process.env.REACT_APP_ENV !== 'production'
                    ? {
                        policy_override: {
                            product: userPolicy?.product,
                            status: userPolicy?.status,
                            department: userPolicy?.department,
                        },
                    }
                    : {}),
            })
            .then((response) => {
                if (cancelled) return
                const byProduct = response?.data?.result?.by_product
                if (byProduct && typeof byProduct === 'object') {
                    setAccessByProduct(byProduct)
                    setAccessMode('api')
                }
            })
            .catch(() => {
                if (cancelled) return
                setAccessMode('fallback')
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.token, userPolicy?.id])

    // Process payment - placeholder function
    const processPayment = (product, enquriy) => {
        if (!!product?.paymentLink) {
            // Append the parameter to the URL
            const finalUrl = `${product.paymentLink}?client_reference_id=${enquriy}`;

            window.location.href = finalUrl;
        } else {
            createInfo('error', 'Payment link not available.');
        }
    }

    // Check overall service access based on user policy
    const checkOverallServiceAccess = () => {
        return !(
            ['SWB OH+', 'SWB OH+ Only'].includes(userPolicy?.product) ||
            !['live', 'expiring'].includes(userPolicy?.status)
        )
    }

    // Check access for specific products
    const checkProductAccess = (productTitle) => {
        if (accessMode === 'api') {
            return !!accessByProduct?.[productTitle]
        }

        // First check overall access
        if (!checkOverallServiceAccess()) return false

        if (['SAS', 'Mutual'].includes(userPolicy?.department)) return true
        if (['SWB OH+', 'SWB OH+ Only'].includes(userPolicy?.department)) return false

        if (![
            'Counselling',
            'Mindfulness',
            'Relaxation Room Live',
            'Nurse Support',
            'Menopause Support',
            'GP Services',
            'SAS Gym',
            'Happier Healthier You',
            'HHY Community',
            'Physiotherapy',
            'Preparing Parents',
            'Food Intolerance Testing',
            'Financial Wellbeing',
            'Manspace',
            'Pupil Yoga',
            'Pastoral Resources',
            'Awards & Programmes',
            'Ask the PT'
        ].includes(productTitle)) return false

        switch (productTitle) {
            case 'Counselling':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'GP Services':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Happier Healthier You':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Physiotherapy':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Preparing Parents':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Pupil Counselling':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            default:
                return true
        }
    }

  const goBack = () => navigate(-1);

  const handleChange = (e, fieldName) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const resolvedName = formData.name || auth?.user?.name || '';
  const resolvedEmail = formData.email ?? auth?.user?.email ?? '';
  const resolvedDob = formData.dob || auth?.user?.date_of_birth || '';
  const resolvedSchoolName = formData.schoolName || auth?.user?.client?.organisation?.name || '';
  const resolvedSchoolPostcode = formData.schoolPostcode || auth?.user?.client?.organisation?.postcode || '';

  const formattedDob = (() => {
    const rawDob = resolvedDob;
    const [year, month, day] = String(rawDob).split('-');
    if (year && month && day) {
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${String(year).padStart(4, '0')}`;
    }
    return rawDob;
  })();

  const isFormValid = resolvedName && resolvedName.trim() !== '' &&
    resolvedEmail && resolvedEmail.trim() !== '' &&
    resolvedDob && resolvedDob.trim() !== '' &&
    formData.contact && formData.contact.trim() !== '' &&
    formData.bestTime && formData.bestTime.trim() !== '' &&
    formData.supportRequest && formData.supportRequest.trim() !== '' &&
    formData.confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const infoObj = {
      user: {
        name: resolvedName,
        email: resolvedEmail,
        phone_number: formData.contact || "",
        dob: resolvedDob,
      },
      school: {
        schoolName: resolvedSchoolName,
        schoolPostcode: resolvedSchoolPostcode,
      },
      contact: formData.bestTime || "",
      extras: formData.supportRequest || "",
    };

    const data = {
      info: JSON.stringify(infoObj),
      name: "General Health",
      product: "financial wellbeing",
      type: "staff",
            status: !hasAccess ? 'Hold' : 'Due',
      staff_id: auth?.user?.client?.staff_id,
      policy_id: userPolicy?.id,
      policy_reference: userPolicy?.reference,
      user_id: auth?.user?.id,
      client_id: auth?.user?.client_id,
    };

        try {
            await axios.post('/tasks/create-from-enquiry', data).then((response) => {
                if (response.data.error) {
                    createInfo('error', "Enquiry Already Exists")
                } else {
                    createInfo('success', "Submitted Successfully.");

                    if (!hasAccess) {
                        setShowPaymentRedirect(true)
                    }
                    setTimeout(() => {
                        if (!hasAccess) {
                            processPayment(product, response.data.result[0].id);
                        }
                    }, 4000)
                }
            });
            setTimeout(() => {
                navigate("/referrals")
            }, 2000);
        } catch (err) {
            console.error(err);
            alert("There was an error submitting the request.");
        } finally {
            setSubmitting(false);
        }
    };

  return (
    <section>
      <div className="max-w-4xl mx-auto p-6 space-y-8 text-portal-dark-blue">
        <h1 className="text-[#41264B] text-2xl font-semibold border-b border-[#41264B]">
          Financial Wellbeing
        </h1>

        <section className="pb-6">
          {!showForm ? (
            <>
              <div className="border border-blue-400 bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3">
                <FontAwesomeIcon icon={faCircleInfo} className="mt-1 text-lg" />
                <p className="text-sm">
                  You must not use this service for emergencies or urgent
                  conditions, as this may delay necessary treatment.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="text-xl font-semibold text-[#41264B] mb-2">
                  How it works
                </h3>
                <p className="text-[#41264B] whitespace-pre-line">
                  Financial wellbeing support can help you feel more in control
                  of money-related stress. Submit an enquiry and we’ll contact
                  you to understand what you need and arrange the most
                  appropriate support.
                </p>
              </div>

              <div className="space-y-4 mt-4">
                <div className="flex items-start gap-3 text-sm text-gray-300">
                  <FontAwesomeIcon
                    icon={faHeartPulse}
                    className="text-[#407cb9] mt-1"
                  />
                  <p>
                    <span className="text-[#407cb9] font-semibold">
                      Budgeting & planning:
                    </span>
                    <span className="text-[#41264B]">
                      {" "}
                      Help building a budget and planning ahead
                    </span>
                  </p>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-300">
                  <FontAwesomeIcon
                    icon={faHeartPulse}
                    className="text-[#407cb9] mt-1"
                  />
                  <p>
                    <span className="text-[#407cb9] font-semibold">
                      Managing debt:
                    </span>
                    <span className="text-[#41264B]">
                      {" "}
                      Guidance on next steps and signposting to specialist
                      support
                    </span>
                  </p>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-300">
                  <FontAwesomeIcon
                    icon={faHeartPulse}
                    className="text-[#407cb9] mt-1"
                  />
                  <p>
                    <span className="text-[#407cb9] font-semibold">
                      Financial stress:
                    </span>
                    <span className="text-[#41264B]">
                      {" "}
                      Support to help reduce money-related anxiety
                    </span>
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowForm(true)}
                className="mt-6 w-full bg-white text-[#41264B] py-3 rounded-lg font-semibold shadow-sm hover:shadow-lg transition-all p-2 border hover:border-[#41264B] hover:bg-[#BCA6C4]"
              >
                Request Help
              </button>
            </>
          ) : (
            <>
              <p className="mt-2 text-[#41264B] whitespace-pre-line">
                Complete the form below to make your request for Financial
                Wellbeing support. All requests are confidential.
              </p>

              <form className="space-y-4 text-[#41264B] p-4 rounded-md mt-6" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm/6 font-semibold">Name</label>
                  <p className="mt-1 text-base text-gray-900 sm:text-sm/6">{resolvedName}</p>
                </div>

                <div>
                  <label className="block text-sm/6 font-semibold">Date Of Birth</label>
                  <p className="mt-1 text-base text-gray-900 sm:text-sm/6">{formattedDob}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Preferred Contact Email for Appointments <span className="text-red-600">*</span></label>
                  <input
                    type="email"
                    className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                    placeholder="Email Address"
                    value={formData.email || ""}
                    onChange={(e) => handleChange(e, "email")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Contact Number <span className="text-red-600">*</span></label>
                  <input
                    type="text"
                    className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                    placeholder="Contact Number"
                    value={formData.contact || ""}
                    onChange={(e) => handleChange(e, "contact")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    When is the best time to contact you?{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <select
                    className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                    value={formData.bestTime || ""}
                    onChange={(e) => handleChange(e, "bestTime")}
                  >
                    <option disabled value="">
                      Select an item
                    </option>
                    <option value="Morning">Morning</option>
                    <option value="Afternoon">Afternoon</option>
                    <option value="Evening">Evening</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Tell us more <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                    placeholder="Please tell us what support you are looking for."
                    value={formData.supportRequest || ""}
                    onChange={(e) => handleChange(e, "supportRequest")}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    name="confirm"
                    id="confirm"
                    type="checkbox"
                    checked={!!formData.confirm}
                    onChange={(e) => handleChange(e, "confirm")}
                  />
                  <label htmlFor="confirm" className="text-sm">
                    By Requesting a service, I am giving my permission for
                    School Wellbeing and third-party service providers to
                    contact me. For more information on how we collect and
                    manage your data, please don't hesitate to contact School
                    Wellbeing on 01773814404.
                  </label>
                </div>

                                {!isFormValid && Object.keys(formData).length > 0 && (
                                    <p className="text-sm text-red-600">Please fill in all required fields before submitting.</p>
                                )}

                                {!hasAccess && (
                                    <p className="text-sm text-red-600">You don’t currently have access to this service. Once you submit the form, you’ll be taken to a payment page to continue.</p>
                                )}

                                <div className="flex items-center justify-between space-x-4">
                                    <button
                                        type="submit"
                                        disabled={!isFormValid || submitting}
                                        className={`w-full py-2 rounded font-semibold mt-4 border-2 transition-all ${!isFormValid
                                            ? 'bg-gray-300 text-gray-400 cursor-not-allowed'
                                            : 'bg-white text-[#41264B] hover:shadow-lg hover:border-[#41264B] hover:bg-[#e6e3ea]'
                                            }`}
                                    >
                                        {!hasAccess && (
                                            <p className="text-sm text-red-600">You don’t currently have access to this service. Once you submit the form, you’ll be taken to a payment page to continue.</p>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={goBack}
                                        className="w-full bg-gray-300 text-gray-800 py-2 rounded font-semibold mt-4 hover:bg-white border-2 border-gray-300"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </section>
            </div>
        </section>
    );
};

export default FinancialWellbeingForm;
